import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { PatchGrid, PatchTooltip, labCss, OCCUPIED_COLOR } from "../pages/colorExploration/ExplorationPlates";

/**
 * Click-to-pick sample selector for the analysis page.
 *
 * Two presentations of the same data, chosen by the caller:
 *
 *   * ``PhysicalPatchGroups`` — the patch group(s) the browsed collection
 *     itself was marked in, laid out exactly like on the plate (row/column).
 *     Used for a generated population, which is marked as one unit.
 *
 *   * ``MemberOriginColumns`` — one visualization for a pooled collection
 *     (input / output), whose members trace back to many separate markings.
 *     One column per iteration the members were marked in, each column a
 *     stack of the members that came from that iteration, ordered by rank.
 *     Reads as a histogram of the pool's provenance; every swatch is
 *     clickable and its tooltip carries batch and plate position.
 *
 * Both scale to fill whatever box they are given (``FitBox`` measures it),
 * so the panel can be pinned to the height of its neighbour and the patches
 * still come out as large as possible.
 */

const SELECTED_OUTLINE = "3px solid #3B6AE6";

// Measures its own box (ResizeObserver) and hands width/height to the render
// prop. The content is absolutely positioned so its size never feeds back
// into the measurement.
function FitBox({ minHeight = 160, children }) {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const update = () => {
      const rect = node.getBoundingClientRect();
      setSize((prev) => {
        const width = Math.floor(rect.width);
        const height = Math.floor(rect.height);
        if (prev.width === width && prev.height === height) return prev;
        return { width, height };
      });
    };
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={ref} sx={{ position: "relative", flex: 1, minHeight, width: "100%" }}>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {size.width > 0 && size.height > 0 ? children(size) : null}
      </Box>
    </Box>
  );
}

function originLabelOf(marking) {
  if (marking?.origin?.label) return marking.origin.label;
  const iteration = marking?.origin?.iteration_number;
  if (iteration !== null && iteration !== undefined) return `Iteration ${iteration}`;
  return "Unknown iteration";
}

/**
 * The browsed collection's own marking(s), side by side, scaled so the
 * largest possible cells still fit the box.
 *
 * Props:
 *   markings: [{patch_group_id, batch_number, member_count, origin}]
 *   gridsByPatchGroup: {patch_group_id: patches-endpoint payload + passKind}
 *   selectedSampleId, onSelectSampleId, isSampleSelectable(sampleId)
 */
function PhysicalPatchGroups({
  markings,
  gridsByPatchGroup,
  selectedSampleId,
  onSelectSampleId,
  isSampleSelectable,
}) {
  const groups = markings
    .map((marking) => ({ marking, grid: gridsByPatchGroup[marking.patch_group_id] }))
    .filter((entry) => entry.grid && entry.grid.rows && entry.grid.columns);
  if (groups.length === 0) return null;

  const cellGap = 4;
  const groupGap = 16;
  const labelHeight = 22;

  return (
    <FitBox minHeight={200}>
      {({ width, height }) => {
        const totalColumns = groups.reduce((sum, entry) => sum + entry.grid.columns, 0);
        const maxRows = Math.max(...groups.map((entry) => entry.grid.rows));
        const widthBudget = width - groupGap * (groups.length - 1) - cellGap * (totalColumns - groups.length);
        const heightBudget = height - labelHeight - cellGap * (maxRows - 1);
        const cell = Math.max(6, Math.floor(Math.min(widthBudget / totalColumns, heightBudget / maxRows)));
        return (
          <Stack direction="row" spacing={`${groupGap}px`} alignItems="flex-start">
            {groups.map(({ marking, grid }) => {
              const gridWidth = cell * grid.columns + cellGap * (grid.columns - 1);
              return (
                <Box key={marking.patch_group_id} sx={{ width: gridWidth }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      height: labelHeight,
                      lineHeight: `${labelHeight}px`,
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {`${originLabelOf(marking)} · Batch ${marking.batch_number} · ${marking.member_count} member${marking.member_count === 1 ? "" : "s"}`}
                  </Typography>
                  <PatchGrid
                    data={grid}
                    passKind={grid.passKind}
                    selectedSampleId={selectedSampleId}
                    isPatchSelectable={(patch) => isSampleSelectable(String(patch.sample_id))}
                    onSelectPatch={(patch) => onSelectSampleId(String(patch.sample_id))}
                    gap={`${cellGap}px`}
                  />
                </Box>
              );
            })}
          </Stack>
        );
      }}
    </FitBox>
  );
}

/**
 * One column per origin iteration; members stacked by rank (best at the
 * bottom, on the baseline). Members whose marking could not be found end up
 * in a trailing "Unmarked" column so the pool stays complete.
 *
 * Props:
 *   members: [{sampleId, rank, originLabel, originIteration, batchNumber,
 *              patch (patches-endpoint entry) | null, passKind}]
 *   selectedSampleId, onSelectSampleId
 */
function MemberOriginColumns({ members, selectedSampleId, onSelectSampleId }) {
  const columns = useMemo(() => {
    const byOrigin = new Map();
    for (const member of members) {
      const key = member.patch ? member.originLabel : "Unmarked";
      if (!byOrigin.has(key)) {
        byOrigin.set(key, {
          key,
          label: key,
          iteration: member.patch ? member.originIteration : Number.POSITIVE_INFINITY,
          phaseOrder: key.startsWith("P") ? 0 : 1,
          members: [],
        });
      }
      byOrigin.get(key).members.push(member);
    }
    const list = Array.from(byOrigin.values());
    for (const column of list) {
      column.members.sort((a, b) => {
        const rankA = a.rank ?? Number.MAX_SAFE_INTEGER;
        const rankB = b.rank ?? Number.MAX_SAFE_INTEGER;
        if (rankA !== rankB) return rankA - rankB;
        return String(a.sampleId).localeCompare(String(b.sampleId));
      });
    }
    list.sort((a, b) => {
      const iterA = a.iteration ?? -1;
      const iterB = b.iteration ?? -1;
      if (iterA !== iterB) return iterA - iterB;
      return a.phaseOrder - b.phaseOrder;
    });
    return list;
  }, [members]);

  if (columns.length === 0) return null;

  const cellGap = 3;
  const columnGap = 8;
  const maxCell = 44;
  // Labels such as "Q12" need ~28px horizontally; narrower columns get the
  // label turned upright so every iteration number stays readable.
  const uprightBelow = 30;
  const longestLabel = Math.max(...columns.map((column) => column.label.length));
  const wanted = selectedSampleId ? String(selectedSampleId) : "";

  return (
    <FitBox minHeight={240}>
      {({ width, height }) => {
        const maxRows = Math.max(...columns.map((column) => column.members.length));
        const widthBudget = width - columnGap * (columns.length - 1);
        const widthCell = widthBudget / columns.length;
        const upright = widthCell < uprightBelow;
        const labelHeight = upright ? Math.max(24, longestLabel * 7 + 6) : 20;
        const heightBudget = height - labelHeight - 4 - cellGap * (maxRows - 1);
        const cell = Math.max(
          6,
          Math.min(maxCell, Math.floor(Math.min(widthCell, heightBudget / maxRows)))
        );
        return (
          <Stack direction="row" spacing={`${columnGap}px`} alignItems="flex-end" sx={{ height: "100%" }}>
            {columns.map((column) => (
              <Stack key={column.key} alignItems="center" justifyContent="flex-end" sx={{ width: cell, height: "100%" }}>
                <Stack direction="column-reverse" spacing={`${cellGap}px`}>
                  {column.members.map((member) => {
                    const tint = member.patch && member.passKind === "COLOR" ? labCss(member.patch.performance) : null;
                    const isSelected = wanted && String(member.sampleId) === wanted;
                    const swatch = (
                      <Box
                        onClick={() => onSelectSampleId(String(member.sampleId))}
                        sx={{
                          width: cell,
                          height: cell,
                          borderRadius: 0.5,
                          border: "1px solid rgba(0,0,0,0.18)",
                          bgcolor: tint || (member.patch ? OCCUPIED_COLOR : "#bdbdbd"),
                          cursor: "pointer",
                          transition: "transform 0.06s ease, box-shadow 0.06s ease",
                          "&:hover": { transform: "scale(1.08)", boxShadow: 3, zIndex: 1 },
                          ...(isSelected
                            ? {
                                outline: SELECTED_OUTLINE,
                                outlineOffset: "1px",
                                boxShadow: "0 0 0 5px rgba(255,255,255,0.9)",
                                zIndex: 2,
                              }
                            : {}),
                        }}
                      />
                    );
                    const lines = [
                      { label: "rank", value: member.rank ?? "–" },
                      { label: "marked in", value: `${member.originLabel} · Batch ${member.batchNumber ?? "?"}` },
                    ];
                    if (member.patch) {
                      lines.push({ label: "position", value: `Row ${member.patch.row} · Col ${member.patch.column}` });
                    }
                    const tooltipPatch = member.patch || {
                      row: "?",
                      column: "?",
                      sample_id: member.sampleId,
                      patch_id: "–",
                      design: {},
                      performance: {},
                    };
                    return (
                      <Tooltip
                        key={member.sampleId}
                        arrow
                        placement="top"
                        title={
                          <PatchTooltip
                            patch={tooltipPatch}
                            heading={member.patch ? `Member of ${member.originLabel}` : "Marking not found"}
                            lines={lines}
                          />
                        }
                      >
                        {swatch}
                      </Tooltip>
                    );
                  })}
                </Stack>
                <Typography
                  variant="caption"
                  sx={{
                    mt: "4px",
                    height: labelHeight,
                    lineHeight: upright ? 1 : `${labelHeight}px`,
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: 11,
                    letterSpacing: 0,
                    whiteSpace: "nowrap",
                    overflow: "visible",
                    ...(upright
                      ? {
                          writingMode: "vertical-rl",
                          transform: "rotate(180deg)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }
                      : {}),
                  }}
                  title={`${column.label}: ${column.members.length} member${column.members.length === 1 ? "" : "s"}`}
                >
                  {column.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        );
      }}
    </FitBox>
  );
}

export { PhysicalPatchGroups, MemberOriginColumns, originLabelOf };
